<template>
    <page-container class="grid grid-cols-12 gap-8">
        <settings-sidebar></settings-sidebar>
        <div class="col-span-12 md:col-span-8 lg:col-span-10">
            <page-header>
                Promotions

                <template v-slot:actions>
                    <vx-button
                        @click="showOverview = !showOverview"
                        data-test="toggle-overview"
                        :aria-label="`${showOverview ? 'Hide' : 'Show'} Overview`"
                        type="button"
                        color="muted-dark"
                        size="lg"
                    >
                        <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                        <font-awesome-icon
                            widthAuto
                            class="hidden! sm:inline-block!"
                            :icon="faCircleQuestion"
                        ></font-awesome-icon>
                        <font-awesome-icon
                            widthAuto
                            class="inline-block! sm:hidden!"
                            :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                        ></font-awesome-icon>
                    </vx-button>
                </template>
            </page-header>

            <promotions-overview v-show="showOverview" class="mb-14"></promotions-overview>

            <promotion-controls
                v-if="list.length || !isEqual(defaultFilters(), fetchParams)"
                v-model="fetchParams"
                @reset="resetFilters"
                @apply="applyFilters"
            ></promotion-controls>

            <page-loading v-if="loading && !list.length"></page-loading>

            <vx-box
                v-if="!loading && !list.length"
                color="info"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                No promotions yet.
                <vx-button href="/settings/integrations/promotions/new">
                    New Promotion <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </vx-box>

            <vx-table v-if="list.length" class="lg:grid-cols-6-auto mt-4" rowBreakpoint="lg">
                <vx-table-row isHeader>
                    <div>ID</div>
                    <div class="xl:col-span-2">Name</div>
                    <div>Allocation</div>
                    <div>Created at</div>
                </vx-table-row>

                <promotion-row
                    v-for="(item, index) in list"
                    :modelValue="list[index]"
                    :key="list[index].id"
                    @deleted="removeFromList($event)"
                ></promotion-row>
            </vx-table>

            <div v-if="fetchParams.cursor" class="mt-8">
                <vx-button
                    @click="loadData()"
                    color="muted-dark"
                    size="lg"
                    class="grow-0"
                    data-test="load-more-btn"
                    :loading="loading"
                >
                    Load More
                    <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                </vx-button>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import {
    faCircleChevronDown,
    faCircleChevronUp,
    faCirclePlus,
    faCircleQuestion,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageHeader from '~/components/general/PageHeader.vue';
import PageContainer from '~/components/general/PageContainer.vue';
import { onMounted, ref } from 'vue';
import PromotionsOverview from '~/components/promotions/PromotionsOverview.vue';
import PromotionControls from '~/components/promotions/PromotionControls.vue';
import PromotionRow from '~/components/promotions/PromotionRow.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { promotionsClient } from '~/services/index';
import PageLoading from '~/components/general/PageLoading.vue';
import SettingsSidebar from '~/components/settings/menus/SettingsSidebar.vue';
import { isEqual } from '~/utils/helpers';

const showOverview = ref(true);

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => ({
    query: '',
    per_page: 10,
    sort_field: 'id',
    sort_dir: 'desc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: undefined,
    };

    loadData();
};

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    return fetchRecords(promotionsClient.getAll(Spark.state.currentTeam.id, { ...fetchParams.value }));
};

const removeFromList = (promotionId) => {
    list.value = list.value.filter((item) => item.id != promotionId);

    if (!list.value.length) {
        loadData();
    }
};
</script>
